/**
 * Sistema unificato per prezzi dinamici nel carrello
 * Gestisce: Boccioni, Bottiglie, Borracce
 */
(function() {
  'use strict';

  if (window.__dynamicPricingCart) return;
  window.__dynamicPricingCart = true;

  console.log('[DynamicPricing] Inizializzazione sistema carrello');

  // ============================================================================
  // CONFIGURAZIONE REGOLE PREZZI
  // ============================================================================

  const PRICING_RULES = {
    boccione: [
      {id: '5-17', label: 'oltre 5-17 boccioni', min: 5, max: 17, price: 1050},
      {id: '18-35', label: 'oltre 18-35 boccioni', min: 18, max: 35, price: 900},
      {id: '36+', label: 'oltre 36+ boccioni', min: 36, max: Infinity, price: 780}
    ]
  };

  // ============================================================================
  // CACHE E STATO
  // ============================================================================

  const productDataCache = {};
  let isUpdatingVariant = false;
  const isCartPage = () => document.body && document.body.classList.contains('template--cart');

  // ============================================================================
  // IDENTIFICAZIONE TIPO PRODOTTO
  // ============================================================================

  const getProductType = (item) => {
    if (!item) return null;
    const title = (item.product_title || '').toLowerCase();
    const variantTitle = (item.variant_title || '').toLowerCase();
    const tags = (item.properties && item.properties._tags) || '';
    
    if (title.includes('boccione') || variantTitle.includes('boccioni')) {
      return 'boccione';
    }
    if (title.includes('bottiglia') || variantTitle.includes('bottiglia') || tags.includes('cat_bottiglie')) {
      return 'bottiglia';
    }
    if (title.includes('borraccia') || variantTitle.includes('borraccia') || tags.includes('cat_borracce')) {
      return 'borraccia';
    }
    return null;
  };

  // ============================================================================
  // CARICAMENTO DATI PRODOTTO
  // ============================================================================

  const getProductData = async (cartItem) => {
    if (!cartItem) return null;
    const handle = cartItem.product_handle || cartItem.handle;
    const cacheKey = handle || cartItem.product_id;
    
    if (productDataCache[cacheKey]) {
      return productDataCache[cacheKey];
    }
    
    try {
      const endpoint = handle 
        ? `${window.Shopify.routes.root}products/${handle}.json` 
        : `${window.Shopify.routes.root}products/${cartItem.product_id}.json`;
      
      const response = await fetch(endpoint);
      const data = await response.json();
      productDataCache[cacheKey] = data;
      return data;
    } catch (error) {
      console.error('[DynamicPricing] Errore caricamento prodotto:', error);
      return null;
    }
  };

  // ============================================================================
  // PARSING VARIANTI CON REGEX
  // ============================================================================

  const parseVariantQuantityRange = (optionValue) => {
    if (!optionValue) return null;
    
    // Patterns supportati:
    // "12-24 pezzi", "100 pezzi", "Oltre 5-17 boccioni", ecc.
    const patterns = [
      /(\d+)-(\d+)\s*(?:pezzi|boccioni)/i,  // Range: "12-24 pezzi"
      /(\d+)\s*(?:pezzi|boccioni)/i,         // Singolo: "100 pezzi"
      /oltre\s*(\d+)-(\d+)/i,                // "Oltre 5-17"
      /oltre\s*(\d+)\+/i                     // "Oltre 36+"
    ];
    
    for (const pattern of patterns) {
      const match = optionValue.match(pattern);
      if (match) {
        const min = parseInt(match[1], 10);
        const max = match[2] ? parseInt(match[2], 10) : Infinity;
        return { min, max };
      }
    }
    
    return null;
  };

  // ============================================================================
  // TROVA REGOLA PREZZO PER QUANTITÀ
  // ============================================================================

  const getPricingRule = (productType, quantity, productData) => {
    if (!productType || !Number.isFinite(quantity)) return null;
    
    // 1. Se abbiamo regole hardcoded (come per boccioni)
    if (PRICING_RULES[productType]) {
      return PRICING_RULES[productType].find(rule => 
        quantity >= rule.min && quantity <= rule.max
      ) || null;
    }
    
    // 2. Altrimenti parsing dinamico dalle varianti
    if (!productData || !productData.product || !productData.product.variants) {
      console.log('[DynamicPricing] DEBUG getPricingRule - productData mancante o invalido:', productData);
      return null;
    }
    
    const variants = productData.product.variants;
    console.log('[DynamicPricing] DEBUG getPricingRule - Parsing', variants.length, 'varianti per qty:', quantity);
    
    // Trova tutte le varianti con range di quantità
    const variantRules = variants
      .map((v, index) => {
        const range = parseVariantQuantityRange(v.option1 || v.option2 || v.option3);
        if (!range) return null;
        
        const priceCents = Math.round(parseFloat(v.price || 0) * 100);
        
        // Calcola max: usa il min della prossima variante - 1, o Infinity per l'ultima
        let max;
        if (index < variants.length - 1) {
          const nextRange = parseVariantQuantityRange(
            variants[index + 1].option1 || variants[index + 1].option2 || variants[index + 1].option3
          );
          max = nextRange ? nextRange.min - 1 : Infinity;
        } else {
          max = Infinity;
        }
        
        const rule = {
          min: range.min,
          max: max,
          price: priceCents,
          variantId: v.id,
          label: v.option1 || v.title
        };
        console.log('[DynamicPricing] DEBUG - Regola creata:', rule);
        return rule;
      })
      .filter(Boolean)
      .sort((a, b) => a.min - b.min);
    
    console.log('[DynamicPricing] DEBUG - Totale regole:', variantRules.length, variantRules);
    
    // Trova la regola che contiene la quantità
    const matchedRule = variantRules.find(rule => 
      quantity >= rule.min && quantity <= rule.max
    ) || null;
    
    console.log('[DynamicPricing] DEBUG - Regola matched per qty', quantity, ':', matchedRule);
    return matchedRule;
  };

  // ============================================================================
  // TROVA VARIANTE CORRETTA PER REGOLA
  // ============================================================================

  const findVariantForRule = (productData, rule, productType) => {
    if (!productData || !productData.product || !rule) return null;
    
    const variants = productData.product.variants;
    console.log('[DynamicPricing] DEBUG findVariantForRule - Cerco variante per regola:', rule);
    
    // 1. Cerca per variantId se disponibile (parsing dinamico)
    if (rule.variantId) {
      const found = variants.find(v => v.id === rule.variantId) || null;
      console.log('[DynamicPricing] DEBUG - Trovata per variantId:', found ? found.id : 'NULL');
      return found;
    }
    
    // 2. Cerca per label (boccioni)
    if (rule.label) {
      const targetLabel = rule.label.toLowerCase();
      const variantByLabel = variants.find(v => {
        const opts = [v.option1, v.option2, v.option3].map(opt => (opt || '').toLowerCase());
        return opts.includes(targetLabel);
      });
      console.log('[DynamicPricing] DEBUG - Trovata per label:', variantByLabel ? variantByLabel.id : 'NULL');
      if (variantByLabel) return variantByLabel;
    }
    
    // 3. Cerca per prezzo (fallback)
    return variants.find(v => {
      const priceCents = Math.round(parseFloat(v.price || 0) * 100);
      const match = Number.isFinite(priceCents) && Math.abs(priceCents - rule.price) <= 1;
      if (match) console.log('[DynamicPricing] DEBUG - Match per prezzo:', v.id, priceCents, '≈', rule.price);
      return match;
    }) || null;
  };

  // ============================================================================
  // AGGIORNAMENTO VARIANTE NEL CARRELLO
  // ============================================================================

  const updateCartItemVariant = async (productData, cartItem, quantity, variantKey) => {
    const productType = getProductType(cartItem);
    if (!productType) return false;
    
    const rule = getPricingRule(productType, quantity, productData);
    if (!rule) {
      console.log('[DynamicPricing] Nessuna regola trovata per', productType, 'qty:', quantity);
      return false;
    }
    
    const correctVariant = findVariantForRule(productData, rule, productType);
    if (!correctVariant) {
      console.log('[DynamicPricing] Variante non trovata per regola:', rule);
      return false;
    }
    
    if (String(correctVariant.id) === String(cartItem.variant_id)) {
      console.log('[DynamicPricing] Variante già corretta:', correctVariant.id);
      return false;
    }
    
    if (isUpdatingVariant) {
      console.log('[DynamicPricing] Aggiornamento già in corso, skip');
      return false;
    }
    
    console.log(`[DynamicPricing] Cambio variante ${productType}: ${cartItem.variant_id} → ${correctVariant.id} (qty: ${quantity})`);
    
    isUpdatingVariant = true;
    try {
      // Rimuovi vecchia variante
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({quantity: 0, id: variantKey})
      });
      
      // Aggiungi nuova variante
      await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          items: [{
            id: correctVariant.id,
            quantity: quantity
          }]
        })
      });
      
      console.log('[DynamicPricing] Variante aggiornata con successo');
      
      // Trigger eventi carrello
      if (typeof BoosterTheme !== 'undefined' && BoosterTheme.cart && BoosterTheme.cart.dispatchListeners) {
        await BoosterTheme.cart.dispatchListeners({change: true});
      } else if (typeof BoosterTheme !== 'undefined' && BoosterTheme.events) {
        BoosterTheme.events.trigger('booster:cart:update');
      }
      
      return true;
    } catch (error) {
      console.error('[DynamicPricing] Errore cambio variante:', error);
      return false;
    } finally {
      isUpdatingVariant = false;
    }
  };

  // ============================================================================
  // RICONCILIAZIONE CARRELLO (VERIFICA TUTTI GLI ITEMS)
  // ============================================================================

  const reconcileCart = async () => {
    if (isUpdatingVariant) {
      console.log('[DynamicPricing] Riconciliazione skip: aggiornamento in corso');
      return;
    }
    
    try {
      console.log('[DynamicPricing] Inizio riconciliazione carrello');
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();
      
      for (const cartItem of cart.items) {
        const productType = getProductType(cartItem);
        if (!productType) continue;
        
        console.log(`[DynamicPricing] Verifica ${productType}: ${cartItem.product_title} (qty: ${cartItem.quantity})`);
        
        const productData = await getProductData(cartItem);
        if (!productData) continue;
        
        await updateCartItemVariant(productData, cartItem, cartItem.quantity, cartItem.key);
      }
      
      console.log('[DynamicPricing] Riconciliazione completata');
    } catch (error) {
      console.error('[DynamicPricing] Errore riconciliazione:', error);
      isUpdatingVariant = false;
    }
  };

  // ============================================================================
  // PATCH BOOSTER THEME (SOLO PER MINICART/AJAX)
  // ============================================================================

  const patchBoosterCart = () => {
    if (isCartPage()) {
      console.log('[DynamicPricing] Pagina carrello: patch non necessario');
      return;
    }
    
    if (typeof BoosterTheme === 'undefined' || !BoosterTheme.cart) {
      console.log('[DynamicPricing] BoosterTheme non pronto, retry...');
      setTimeout(patchBoosterCart, 200);
      return;
    }
    
    if (BoosterTheme.cart.__dynamicPricingPatched) {
      console.log('[DynamicPricing] BoosterTheme già patched');
      return;
    }
    
    BoosterTheme.cart.__dynamicPricingPatched = true;
    console.log('[DynamicPricing] Patching BoosterTheme.cart.updateQuantity');
    
    const originalUpdateQuantity = BoosterTheme.cart.updateQuantity 
      ? BoosterTheme.cart.updateQuantity.bind(BoosterTheme.cart) 
      : null;
    
    if (originalUpdateQuantity) {
      BoosterTheme.cart.updateQuantity = async function(event, variant, quantity, forced) {
        const target = event && event.target ? event.target : null;
        const variantKey = target && target.dataset ? target.dataset.variantId : null;
        const newQuantity = forced ? quantity : (parseInt(target && target.value, 10) || 1);
        
        if (variantKey) {
          try {
            const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
            const cart = await cartResponse.json();
            const cartItem = cart.items.find(item => item.key === variantKey);
            
            if (cartItem) {
              const productType = getProductType(cartItem);
              if (productType) {
                console.log(`[DynamicPricing] Intercettato cambio qty ${productType}:`, newQuantity);
                const productData = await getProductData(cartItem);
                const variantChanged = await updateCartItemVariant(productData, cartItem, newQuantity, variantKey);
                if (variantChanged) {
                  console.log('[DynamicPricing] Variante cambiata, skip update originale');
                  return;
                }
              }
            }
          } catch (error) {
            console.error('[DynamicPricing] Errore intercettazione:', error);
          }
        }
        
        return originalUpdateQuantity(event, variant, quantity, forced);
      };
    }
    
    // Listener per riconciliazione automatica
    document.addEventListener('booster:cart:update', reconcileCart);
    console.log('[DynamicPricing] Listener booster:cart:update registrato');
  };

  // ============================================================================
  // INIZIALIZZAZIONE
  // ============================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchBoosterCart);
  } else {
    patchBoosterCart();
  }
  
  // Esporta funzioni per uso nella pagina carrello
  window.DynamicPricingCart = {
    getProductType,
    getProductData,
    getPricingRule,
    updateCartItemVariant,
    reconcileCart,
    isUpdating: () => isUpdatingVariant
  };
  
  console.log('[DynamicPricing] Sistema pronto');
})();

